import React, { useEffect, useMemo, useState } from 'react'
import logoSandre from 'assets/pictures/logo_sandre.png'
import { push } from '@lagunovsky/redux-react-router'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import { getDataWithHeaders } from 'utils/ExportDataUtil'
import { sandreFractionDefaultLink } from '../../../../conf/SieauConstants'
import HomeAction from '../../../../home/actions/HomeAction'
import {
    PATH_REFERENCIAL,
    PATH_REFERENCIAL_FRACTION,
    PATH_REFERENCIAL_FRACTION_NEW,
} from '../../../../home/constants/RouteConstants'
import ReferencialAction from '../../../action/ReferencialAction'
import FractionAction from '../actions/FractionAction'
import {
    nbPerPageLabel,
    REFERENCIAL_TYPE_NAME,
    REFERENTIAL_JOBSYNC_ID,
} from 'referencial/constants/ReferencialConstants'
import Input from 'components/forms/Input'
import Select from 'components/forms/Select'
import useSessionStorage from 'utils/customHook/useSessionStorage'
import { hasValue } from 'utils/NumberUtil'
import { searchAllCharacters } from 'utils/StringUtil'
import { Button, Card, CardContent, Grid2, Stack } from '@mui/material'
import { getStatusInformation } from 'referencial/util/StatusUtils'
import SupportAction from '../../support/actions/SupportAction'
import { reloadJobSync } from '../../../util/ReferencialUtils'
import { CardTable } from '../../../../components/datatable/NewTable'
import { CardTitle } from 'components/card/NewCard'
import JobSyncStatus from '../../JobSyncStatus'

const headers = ['code', 'name', 'support', 'status']

const FractionsApp = () => {
    const {
        fractions,
        supports,
        referencialStatus,
    } = useSelector(store => ({
        fractions: store.FractionReducer.fractions,
        supports: store.SupportReducer.supports,
        referencialStatus: store.ReferencialReducer.referencialStatus,
    }), shallowEqual)

    const [filter, setFilter] = useSessionStorage(`referencial_${REFERENCIAL_TYPE_NAME.fraction}`, {})
    const [filterTmp, setFilterTmp] = useState(filter)
    const [jobSync, setJobSync] = useState(undefined)

    useEffect(() => {
        reloadJobSync(REFERENTIAL_JOBSYNC_ID.fraction, setJobSync)
    }, [])

    useEffect(() => {
        if (jobSync && jobSync.executions[0]) {
            if (!jobSync.executions[0].statusCode) {
                setTimeout(() => {
                    reloadJobSync(REFERENTIAL_JOBSYNC_ID.fraction, setJobSync)
                }, 5000)
            }
        }
    }, [jobSync])

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(FractionAction.fetchFractions())
        dispatch(HomeAction.setHelpLink('', ''))
        if (!referencialStatus.length) {
            dispatch(ReferencialAction.fetchReferencialStatus())
        }
        if (!supports.length) {
            dispatch(SupportAction.fetchSupports())
        }
    }, [])

    const fractionByStatus = useMemo(() => hasValue(filter.status) ? fractions.filter(h => parseInt(h.status) === filter.status) : fractions, [filter.status, fractions])
    const data = useMemo(() => {
        return fractionByStatus
            .filter((obj) => filter.support ? obj.supportCode === filter.support : true)
            .filter((obj) => searchAllCharacters(headers.map((key) => obj[key]).join())
                .includes(searchAllCharacters(filter.searchValue || '')))
    }, [fractionByStatus, filter.searchValue, filter.support])

    const dataFormatted = useMemo(() => data.map(s => ({
        ...s,
        code: { value: s.code, sortValue: s.code },
        name: { value: s.name, sortValue: s.name },
        support: { value: s.supportCode ? `[${s.supportCode}] ${supports.find(su => su.code === s.supportCode)?.name}` : '', sortValue: s.supportCode },
        status: { value: s.status ? getStatusInformation(s.status.toString()) : '', sortValue: s.status },
        colorLine: s.actif === '1' ? undefined : '#c5c5c5',
    })), [data, supports])

    useTitle(() => [{
        title: i18n.referencials,
        href: PATH_REFERENCIAL,
    }, {
        title: i18n.fractions,
        href: PATH_REFERENCIAL_FRACTION,
    }], [])

    useActions(() => {
        return {
            new: () => dispatch(push(PATH_REFERENCIAL_FRACTION_NEW)),
            referencialActions: {
                actions: {
                    export: {
                        data: getDataWithHeaders(data, headers),
                        titleFile: i18n.fractionsList,
                    },
                    sync: {
                        updateAction: options => dispatch(ReferencialAction.launchSyncJob(REFERENTIAL_JOBSYNC_ID.fraction, options, () => reloadJobSync(REFERENTIAL_JOBSYNC_ID.fraction, setJobSync))),
                        withInsertAllOption: true,
                    },
                    purge: {
                        onPurge: () => dispatch(FractionAction.purgeFraction()),
                    },
                },
            },
            links: [{
                href: sandreFractionDefaultLink,
                img: logoSandre,
                label: i18n.sandre.toUpperCase(),
            }],
        }
    }, [data])

    return (
        <Stack margin={1} spacing={1}>
            <Card>
                <CardTitle title={i18n.criterias} color='bannerColor'/>
                <CardContent>
                    <Grid2 size={12} container spacing={1} paddingTop={1}>
                        <Grid2 size={2}>
                            <Input
                                title={i18n.search}
                                value={filterTmp.searchValue}
                                onChange={v => setFilterTmp({ ...filterTmp, searchValue: v })}
                                onEnterKeyPress={() => setFilter({ ...filterTmp })}
                            />
                        </Grid2>
                        <Grid2 size={2}>
                            <Select
                                options={referencialStatus}
                                label={i18n.status}
                                onChange={v => setFilterTmp({ ...filterTmp, status: v })}
                                value={filterTmp.status}
                                nullLabel='&nbsp;'
                            />
                        </Grid2>
                        <Grid2 size={2}>
                            <Select
                                options={supports}
                                label={i18n.support}
                                onChange={v => setFilterTmp({ ...filterTmp, support: v })}
                                value={filterTmp.support}
                                nullLabel='&nbsp;'
                            />
                        </Grid2>
                        <Grid2 size='grow'>
                            <JobSyncStatus jobSync={jobSync}/>
                        </Grid2>
                        <Grid2 size={1} paddingTop={2}>
                            <Button className='right' onClick={() => setFilter({ ...filterTmp })} variant='contained' color='primary'>
                                {i18n.search}
                            </Button>
                        </Grid2>
                    </Grid2>
                </CardContent>
            </Card>
            <CardTable
                title={i18n.fractionsList}
                color='bannerColor'
                rowsPerPageOptions={nbPerPageLabel}
                rows={dataFormatted}
                headers={headers}
                onClickRow={fraction => dispatch(push(`/referencial/fraction/${fraction.id}/dashboard`))}
            />
        </Stack>
    )
}

export default FractionsApp
